import axios, { AxiosError, AxiosInstance } from 'axios';
// Aliased: the interceptors below take a parameter also called `config`.
import { config as appConfig } from '../config/environment';
import { clearCachedBranding } from '../library/brandingCache';
import { clinicAdminLink } from '../library/constants';
import { IJwtClaims, readJwtClaims } from '../library/jwtClaims';
import { IRenewedToken } from '../library/models/renewedToken';
import { AuthResponse } from '../types';

/**
 * Everything the app does about the session token on the wire, in one place: sending it,
 * keeping it alive while the app is in use, and noticing when the server stops accepting it.
 *
 * Both axios layers -- `BaseService` and the legacy `apiService` -- call `attachSession` and
 * do nothing about auth themselves. They used to carry a copy each of the two interceptors
 * below, and every change to auth handling had to be made twice.
 *
 * **A token lasts a day, and a session in use never runs into that.** Any request the app
 * makes is taken as the person being there: once the token is more than `RENEW_AFTER_MS` old,
 * the next request also asks `POST /auth/refresh` for a new one, in the background. So the
 * token's lifetime is how long somebody may be *away* before they have to sign in again, not
 * how long they may work. The server stops renewing a session 30 days after its sign-in.
 *
 * This is a module rather than a service because the renewal must not pass through the
 * interceptors it is called from, and because the session is one thing however many axios
 * instances there are -- so its state lives here, once.
 */

/**
 * How old a token may get before the next request renews it. An hour keeps the idle window
 * within an hour of the token's full lifetime, at one extra request per hour per browser.
 */
export const RENEW_AFTER_MS: number = 60 * 60 * 1000;

/** How long to leave it after a renewal that failed for some reason other than a refusal. */
const RETRY_AFTER_MS: number = 60 * 1000;

const BEARER_PREFIX = 'Bearer ';

/**
 * The one request that is not the person being there. The header asks for this every minute
 * for as long as a tab is open, for patients as well as staff, so counting it as use would
 * turn "a day away and you sign in again" into thirty days for anybody who left a tab open.
 * It still carries the token, and a refusal still ends the session.
 */
const BACKGROUND_POLL_URL = '/notifications/unread-count';

/**
 * **An administrator acting as one of their therapists does so in one tab.** The therapist's
 * session is kept under the same three keys, `authToken`, `user` and `userRole`, but in this
 * tab's `sessionStorage`, where it shadows the administrator's own in `localStorage` -- which
 * every other tab goes on using, and which nothing here writes while it is shadowed.
 *
 * Going in and coming back are both a full page load, never a swap in place: nothing cached,
 * in flight or half-rendered for one person can then be drawn under the other's name.
 */
export const isImpersonating = (): boolean => sessionStorage.getItem('authToken') !== null;

/** Where the session this tab is using lives. */
export const sessionStore = (): Storage => (isImpersonating() ? sessionStorage : localStorage);

/** Drops this tab's impersonation without a word to the API, for a token already unusable. */
export const forgetImpersonation = (): void => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('userRole');
};

/** Takes up the therapist's session the API just handed the administrator, and opens `to`. */
export const enterImpersonation = (session: AuthResponse, to: string): void => {
    sessionStorage.setItem('authToken', session.token);
    sessionStorage.setItem('user', JSON.stringify(session.user));
    sessionStorage.setItem('userRole', session.role);
    window.location.assign(to);
};

/**
 * Ends this tab's impersonation: the API is told first, with the token in hand -- plain axios,
 * the `signOutOfPush` way, because nothing in this tab will carry that token a moment later --
 * so it stops working rather than lingering for the rest of its hour. Never rejects: a failure
 * leaves a token the hour still bounds.
 */
export const endImpersonation = (): Promise<void> => {
    const token: string | null = sessionStorage.getItem('authToken');
    forgetImpersonation();

    if (!token) {
        return Promise.resolve();
    }

    return axios
        .delete(`${appConfig.apiBaseUrl}/auth/impersonation`, { headers: { Authorization: `${BEARER_PREFIX}${token}` } })
        .then(() => undefined, () => undefined);
};

/** Back to the administrator's own session, on `to`. */
export const leaveImpersonation = (to: string): void => {
    void endImpersonation().then(() => window.location.assign(to));
};

export type SessionEvent =
    /** The stored token was replaced by a newer one for the same session. */
    | { type: 'renewed'; token: string }
    /** The server refused the stored token, and the stored session has been cleared. */
    | { type: 'ended' };

type SessionListener = (event: SessionEvent) => void;

const listeners = new Set<SessionListener>();

/**
 * Tells React what happened out here, where there is no dispatch. `AuthProvider` is the one
 * subscriber. Returns the unsubscribe.
 */
export const onSessionEvent = (listener: SessionListener): (() => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

const emit = (event: SessionEvent): void => listeners.forEach((listener: SessionListener) => listener(event));

/**
 * Ends the stored session, if the token the server just refused is still the one stored.
 *
 * The comparison is the whole point. A 401 for a request that carried no token is a wrong
 * password, not a dead session. And an answer can arrive late: for a token that has since
 * been renewed, or for the account that was signed in before this one. Neither says anything
 * about the session that is stored now, and both used to wipe it.
 */
const endSessionIfCurrent = (refusedToken: string | null): void => {
    if (!refusedToken) {
        return;
    }

    // An hour is up, the administrator went back in another way, or lost their role: this tab
    // returns to their own session, which nothing about this refusal says anything against.
    if (sessionStorage.getItem('authToken') === refusedToken) {
        forgetImpersonation();
        window.location.assign(clinicAdminLink);
        return;
    }

    if (localStorage.getItem('authToken') !== refusedToken) {
        return;
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    // Cleared alongside the session: the signed-out shell is Carefull's.
    clearCachedBranding();

    emit({ type: 'ended' });
};

let renewing: boolean = false;
let lastFailureAt: number = 0;

const isDueForRenewal = (token: string, now: number): boolean => {
    const claims: IJwtClaims | null = readJwtClaims(token);

    if (typeof claims?.iat !== 'number' || typeof claims.exp !== 'number') {
        return false;
    }

    // An expired token is not renewed, it is refused -- by the request this one rides on.
    return claims.exp * 1000 > now && now - claims.iat * 1000 >= RENEW_AFTER_MS;
};

/**
 * Asks for a new token if this one is old enough, without holding anything up: the request
 * that triggered it goes out with the token it has, which is still good.
 *
 * One at a time, however many requests a page fires at once.
 */
const renewIfDue = (token: string): void => {
    const now: number = Date.now();

    if (renewing || now - lastFailureAt < RETRY_AFTER_MS || !isDueForRenewal(token, now)) {
        return;
    }

    renewing = true;

    // Plain axios, not one of the app's instances: this must not go back through the
    // interceptors below, which would renew the renewal.
    axios
        .post<IRenewedToken>(`${appConfig.apiBaseUrl}/auth/refresh`, undefined, {
            headers: { Authorization: `${BEARER_PREFIX}${token}` },
        })
        .then(({ data }) => {
            if (typeof data?.token !== 'string') {
                lastFailureAt = Date.now();
                return;
            }

            // Signed out, or signed in as somebody else, while this was in flight: the answer
            // belongs to a session that is no longer the stored one.
            if (localStorage.getItem('authToken') !== token) {
                return;
            }

            // The token and only the token. `user` and `userRole` belong to the /users/me
            // refresh in AuthContext, which knows about edits made since.
            localStorage.setItem('authToken', data.token);
            emit({ type: 'renewed', token: data.token });
        })
        .catch((error: AxiosError) => {
            if (error.response?.status === 401) {
                endSessionIfCurrent(token);
                return;
            }

            // Offline, a 5xx, a disabled account's 403: the token in hand is as good as it
            // was, so keep it and ask again later rather than on every request.
            lastFailureAt = Date.now();
        })
        .finally(() => {
            renewing = false;
        });
};

const tokenSentWith = (authorization: unknown): string | null =>
    typeof authorization === 'string' && authorization.startsWith(BEARER_PREFIX)
        ? authorization.slice(BEARER_PREFIX.length)
        : null;

/** Installs the session's two interceptors on an axios instance. */
export const attachSession = (api: AxiosInstance): void => {
    api.interceptors.request.use(
        (config) => {
            const token = sessionStore().getItem('authToken');
            if (token) {
                config.headers.Authorization = `${BEARER_PREFIX}${token}`;

                // Acting as somebody is an hour that is never renewed; the API refuses it.
                if (config.url !== BACKGROUND_POLL_URL && !isImpersonating()) {
                    renewIfDue(token);
                }
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    api.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
            // 401 is "nobody is signed in" and only that; "signed in and not allowed" is a
            // 403 and leaves the session alone. See UserSecurityConfig on the server.
            if (error.response?.status === 401) {
                endSessionIfCurrent(tokenSentWith(error.config?.headers?.Authorization));
            }
            return Promise.reject(error);
        }
    );
};
